import React, {
    createContext,
    ReactElement,
    ReactNode,
    useContext
} from 'react';
import { useStateApi } from '../common/hooks';
import { StateApiFactory } from '../types';

interface Store<A> {
    StoreProvider: (props: { children?: ReactNode }) => ReactElement;
    useStore: () => A;
}

const createStore = <S, A>(
    storeApiFactory: StateApiFactory<S, A>,
    initialState: S
): Store<A> => {
    // The actual initial context (or initial store) is defined by each different type of store we create,
    // and is only really "created" when we call useStateApi inside our StoreProvider, so we don't have access to it
    // at this point and just set it to null
    const StoreContext = createContext<A>((initialState as unknown) as A);

    const StoreProvider = (props: { children?: ReactNode }): ReactElement => {
        const store = useStateApi(storeApiFactory, initialState);

        return (
            <StoreContext.Provider value={store}>
                {props.children}
            </StoreContext.Provider>
        );
    };

    let useStore = (): A => {
        return useContext(StoreContext);
    };

    return { StoreProvider, useStore };
};

export default createStore;
